import { QuestionInputType } from "@/types/agent"
import {
  ActionItem,
  AttachmentDataType,
  AttachmentType,
  FlightTripTypes,
  Intent,
} from "@/types/chat"
import {
  CollectionImportType,
  CollectionRef,
  CollectionTrigger,
} from "@/types/collection"
import { DayObject } from "@/types/date"
import {
  EntityRef,
  EventActorRef,
  InspirationRef,
  PlaceRef,
} from "@/types/entity"
import { ExploreType, SocialFilters } from "@/types/explore"
import { FlightBookingStatus } from "@/types/flights/flight-booking"
import {
  BoardType,
  DistributionType,
  HotelBookingError,
  HotelBookingRef,
  HotelBookingStatus,
  PaymentType,
  RefundType,
} from "@/types/hotels/hotels"
import {
  InspirationStatus,
  InspirationTemplate,
  InspirationWhere,
} from "@/types/inspiration"
import { ItineraryEventType } from "@/types/itinerary"
import { Notification } from "@/types/notification"
import {
  BedConfig,
  EventCategory,
  GeoCategory,
  HotelCategory,
  PlaceCategory,
} from "@/types/place"
import type { CalendarSyncProvider } from "@/types/socket"
import type { TravelStoryTemplateName } from "@/types/travel-story"
import {
  Budget,
  Room,
  ShareKind,
  TravelerGroup,
  TripRef,
  TripVibe,
} from "@/types/trip"
import type { TimeFrame } from "@/types/trip"
import { UserRef } from "@/types/user"
import { Nullable, Optional } from "@/types/util"

import type { AppHomeSearchType } from "@/lib/app-home-search"
import type { UIWhere } from "@/lib/itinerary-action-types"
import type { TravelAssistantEntrySource } from "@/lib/pending-auth-action"
import { AddToTripTrackingContext } from "@/components/add-to-trip-button"
import { InspirationBotGenerateOption } from "../inspiration-api"
import { PromoDialogType } from "../promo-dialog-manager"
import { VoiceGuideScript } from "../voice-api"

export const AmplitudeEmptyValue = "--EMPTY--"

export interface AmplitudeTest {
  name: string
  value: string
}

// Event with no props
export type AmplitudeEvent =
  | "ViewTripSlideOut"
  | "ViewTripAdvice"
  | "FoundTripReviewOpen"
  | "FoundTripDismiss"
  | "FoundBookingDismiss"

  // consolidate below 3 into ViewAuthPage
  | "ViewAuthPage"
  | "ViewResetPasswordPage"
  | "ViewAboutPage"
  | "ViewTeamPage"
  | "ViewPressPage"
  | "ViewHelpPage"
  | "ViewCreatorProgramPage"
  | "ViewContactUsPage"
  | "ViewMediaKitPage"
  | "ViewPrivacyPolicyPage"
  | "ViewCookiePolicyPage"
  | "ViewCookiePreferencesPage"
  | "ViewTermOfServicePage"
  | "ViewCreatePage"
  | "ViewAdminPage"
  | "ViewTripsPage"
  | "ViewTripsCalendarPage"
  | "ViewStoriesPage"
  | "ViewSavedPage"
  | "ViewProfilePage"
  | "ViewPublicProfilePage"
  | "ViewEditProfilePage"
  | "ViewEditYourAccoutPage"
  | "ViewEditLanguageRegionPage"
  | "ViewEditNotificationSettingsPage"
  | "ViewEditRewardProgramsPage"
  | "ViewEditTravelersPage"
  | "ViewEditPaymentPage"
  | "ViewEditConnectedAccountsPage"
  | "ViewConnectedCalPreviewPage"
  | "GoogleCalendarConnectInitiated"
  | "GoogleCalendarConnectCompleted"
  | "GoogleCalendarConnectFailed"
  | "GoogleCalendarDisconnected"
  | "ViewEditCookiePreferencesPage"
  | "ViewUnknownEditProflePage"
  | "NotificationGroupToggled"
  | "NotificationSettingUpdated"
  | "ViewBusinessOverviewPage"
  | "ViewBusinessPricingPage"
  | "ViewBusinessHotelsPage"
  | "ViewBusinessDestinationsPage"
  | "ViewBusinessHowItWorksPage"
  | "ViewInspirationPage"
  | "ViewAppHomePage"

  // Events
  | "SignIn"
  | "SignOut"
  | "SignOutFail"
  | "RegisterUser"
  | "ForgotPasswordSubmit"
  | "ResetPasswordSubmit"
  | "ClickTryPrompt"
  | "ClickAskMindtrip"
  | "StartANewChatButton"
  | "GenerateItineraryButton"
  | "CreateItineraryButton"
  | "ContinueChatButton"

  // Media Kits
  | "MediaAllDownload"
  | "MediaBrandKitDownload"
  | "MediaProductImagesDownload"
  | "MediaHeadShotsDownload"

  // User OptOut or OptIn
  | "UserOptOut"
  | "UserOptIn"

  // User Profiles
  | "UserProfileUpdate"
  | "UserProfileEmailEdit"
  | "UserProfileAddressEdit"
  | "UserProfilePhoneEdit"
  | "UserProfileLanguageEdit"
  | "UserProfileBirthdayEdit"
  // UserProfile cancel
  | "UserProfileEmailCancel"
  | "UserProfileAddressCancel"
  | "UserProfilePhoneCancel"
  | "UserProfileLanguageCancel"
  | "UserProfileBirthdayCancel"
  // UserProfile saved
  | "UserProfilePersonalizationUpdate"
  | "UserProfilePaymentUpdate"
  | "UserProfileEmailUpdate"
  | "UserProfileAddressUpdate"
  | "UserProfilePhoneUpdate"
  | "UserProfileLanguageUpdate"
  | "UserProfileRegionUpdate"
  | "UserProfileBirthdayUpdate"
  | "UserProfileDeleteAccount"
  | "UserProfileCookiePreferencesUpdate"

  // Assistant setup
  | "SetUpAssistantFormStart"
  | "VoiceInterviewStart"
  | "VoiceInterviewComplete"

  // Chat
  | "ChatCreate"
  | "SkipThinking"
  | "StopBotResponse"
  | "ViewThinking"
  | "ClickMapInfoToggle"
  | "ClickCustomizeFlights"

  // Flights
  | "FlightBookingNew"
  | "FlightSeatMapOpen"

  // Creator's landing page
  | "ViewCreatorJoinWaitlistLandingPage"
  | "ViewCreatorJoinProgramLandingPage"
  | "ViewCreatorCreateLinkLandingPage"
  | "ViewCreatorReportPage"
  | "ViewCreatorAdminReportPage"

  // Creator's landing page button
  | "CreatorJoinWaitlist"
  | "CreatorJoinProgram"
  | "CreatorJoinProgramSignUp"

  // Creator's join wizard
  | "ViewCreatorHandlePage"
  | "ViewCreatorPaymentPage"
  | "ViewCreatorTermsOfServicePage"
  | "ViewCreatorHandleAndTermsPage"
  | "CreatorAcceptTermOfService"
  | "CreatorSavePayment"

  // Creator's create link wizard
  | "ViewCreatorChooseGetStartedPage"
  | "ViewCreatorChooseSourcePage"
  | "ViewCreatorMagicLinkPage"
  | "ViewCreatorUploadFilePage"
  | "ViewCreatorDefinitionPage"
  | "ViewCreatorLayoutPage"
  | "ViewCreatorLocationPage"
  | "ViewCreatorLinkGeneratedPage"

  // Tabs
  | "ViewCreatorLinksTab"
  | "ViewCreatorGuidesTab"
  | "ViewCreatorEarningsTab"
  | "ViewCreatorCommissionsTab"
  | "ViewCreatorThatchCommissionsTab"

  // Admin Tabs
  | "ViewAdminDraftsTab"
  | "ViewAdminPublishedTab"
  | "ViewAdminReportTab"
  | "ViewAdminEarningsTab"
  | "ViewAdminCreatorsTab"
  | "ViewCreatorNeedHelp"

  // Folio
  | "ViewReceiptsPage"

  // Explore
  | "ExploreSearchClick"

  // Collections
  | "CollectionChange"
  | "CollectionView"
  | "CollectionAddPlace"
  | "CollectionRemovePlace"
  | "CollectionAddCollaborator"
  | "CollectionRemoveCollaborator"
  | "CollectionJoin"
  | "CollectionLeave"
  | "CollectionBulkAddPlaces"
  | "CollectionReorderPlace"

  // Create anonymous user
  | "AmplitudeInitialized"
  | "CreateAnonUser"

  // Server events
  | "ChatBotMessageAdd"
  | "FlightAgentCall"
  | "ItineraryGenerate"
  | "TripUserCreated"
  | "UserRegister"

// ----------------------------------------------------------------------------
// Event with props
// ----------------------------------------------------------------------------
export type AmplitudeEventWithProps =
  | "AuthStepView"
  | "AuthStepSubmit"
  | "AuthStepResult"
  | "PhoneAuthSubmit"
  | "PhoneAuthResult"
  | "PhoneAuthUseEmail"
  | "PhoneAuthRestart"
  | "PhoneAuthEmailSkipped"
  | "SignIn"
  | "RegisterUser"
  | "RouteChange"
  | "FoundTripAccept"
  | "FoundBookingAdd"
  | "VoiceInterviewSkip"
  | "SetUpAssistantStart"
  | "SetUpAssistantStepView"
  | "SetUpAssistantStepTransition"
  | "SetUpAssistantExit"
  | "SetUpAssistantComplete"
  | "SetUpAssistantCompletionContinue"
  | "ClickMemoryToast"
  | "NotificationClick"
  | "ConnectCalendarStart"
  | "ConnectCalendarComplete"
  | "GoogleCalendarConnectFailed"
  | "CalendarSyncPhaseCompleted"
  | "CalendarSyncPhaseFailed"
  | "TravelStoryOpened"
  | "TravelStoryFrameViewed"
  | "TravelStoryClosed"
  | "TravelStoryDownload"
  | "TravelStoryShare"
  | "TravelStoryPlanTripClicked"
  | "UpdateAssistant"
  | "ViewTripSheetItinerary"
  | "ViewTripSheetCalendar"
  | "ViewTripSheetBookings"
  | "ViewTripSheetMedia"
  | "ViewTripSheetIdeas"
  | "ViewTripSheetDetails"
  | "ViewTripSheetKeyDetails"
  | "ViewTripSheetChats"
  | "ViewTripSheetDisruptions"
  | "ViewHomePage"
  | "ClickHomePageCta"
  | "ViewChatPage"
  | "ViewChatGroupPage"
  | "ViewChatSearchPage"
  | "ChatRemove"
  | "ChatRename"
  | "ChatConvertedToGroup"
  | "ChatUserMessageAdd"
  | "ChatFollowUpMessageRequested"
  | "VoiceSessionStart"
  | "VoiceSessionEnd"
  | "ViewClarifyingQuestions"
  | "SubmitClarifyingQuestions"
  | "SkipClarifyingQuestions"
  | "ClickClarifyingQuestionOther"
  | "ClickChatPill"
  | "ClickAppHomeSearchPill"
  | "SubmitVerticalForm"
  | "TopSuggestionInteraction"
  | "OpenInChat"
  | "GuideIdeasOpen"
  | "GuideIdeasMessage"
  | "GuideIdeasAddPlace"
  | "CollectionIdeasOpen"
  | "CollectionIdeasMessage"
  | "CollectionIdeasAddPlace"
  | "GuideVersionHistoryOpen"
  | "GuideVersionRestore"
  | "PriceAlertClick"
  | "CustomAgentInteraction"
  | "LocationPicker"
  | "RefineForYouStripe"
  | "ClickTripSuggestion"
  | "ClickActionPill"
  | "ClickTripAdviceActionPill"
  | "DismissTripAdvice"
  | "SnoozeTripAdvice"
  | "UnsnoozeTripAdvice"
  | "ViewTripAdviceCard"
  | "ViewTripHub"
  | "LocationSelect"
  | "IntentLocationUpdate"
  // | "IntentDatesUpdate" -> replaced by IntentTimeFrameUpdate
  | "IntentTimeFrameUpdate"
  | "IntentTravelersUpdate"
  | "IntentBudgetUpdate"
  | "ChatHotelSearch"
  | "ChatRestaurantSearch"
  | "ChatEventSearch"
  | "ChatAttractionSearch"
  | "ChatActivitySearch"
  | "UserSearch"
  | "UserFollow"
  | "UserUnfollow"
  | "ForgotPasswordFail"
  | "ResetPasswordFail"
  | "RegisterUserFail"
  | "SignInFail"
  | "ChatCreate"
  | "ChatSwitch"
  | "MajorStopsUpdate"
  | "TripCreate"
  | "TripSpecificsExtracted"
  | "TripUpdate"
  | "TripDestinationUpdate"
  | "TripRoadtripUpdate"
  | "TripTimeFrameUpdate"
  | "TripTravelersUpdate"
  | "TripBudgetUpdate"
  | "TripVibeUpdate"
  | "TripJoined"
  | "TripKeyDetailUserAdd"
  | "TripKeyDetailUserUpdate"
  | "TripKeyDetailUserRemove"
  | "TripKeyDetailSuggestionAccepted"
  | "TripKeyDetailSuggestionRejected"
  | "PlaceFavoriteAdd"
  | "PlaceFavoriteRemove"
  | "EntityListen"
  | "ViewPlaceDetailSlideOut"
  | "ViewPlaceDetailAllPhotos"
  | "ViewPlaceDetailStandalone"
  | "ViewPlaceDetailSection"
  | "ViewEventActorDetailSlideOut"
  | "ViewEventActorDetailStandalone"
  | "UserFeedbackCreate"
  | "MessageCopy"
  | "MessageListen"
  | "MapHighlightAdd"
  | "MapHighlightRemove"
  | "ItineraryAdd"
  | "ItineraryRemove"
  | "ItineraryUpdate"
  | "ItineraryRemoveScheduledEvents"
  | "ItineraryRemoveIdeas"
  | "ItineraryUndo"
  | "ItineraryRedo"
  | "ItineraryEventDuplicated"
  | "AIItineraryGenerate"
  | "ItineraryPublish"
  | "CollectionPublish"
  | "AIQuestionAsk"
  | "AIPlaceAsk"
  | "HotelBookingView"
  | "RestaurantBookingReserveForMeClick"
  | "RestaurantBookDirect"
  | "PlaceBookDirect"
  | "HotelChooseRoomView"
  | "ActivitiesAndToursView"
  | "ActivitiesView"
  | "ToursView"
  | "HotelBookingCreate"
  | "HotelBookingError"
  | "OutOfRangeBooking"
  | "HotelBookingConfirmAndPay"
  | "HotelBookingConfirmAndPayError"
  | "HotelBookingPaymentStep"
  | "HotelBookingCancel"
  | "HotelBookingCancelError"
  | "ViewHotelBookingPage"
  | "ViewHotelBookingConfirmationPage"
  | "ViewHotelBookingErrorPage"
  | "ViewHotelBookingDetailsPage"
  | "ViewHotelRoomsPage"
  | "AgentsPageView"
  | "CustomAgentTurnedOn"
  | "CustomAgentRunNow"
  | "ViewPriceAlertsPage"
  | "PriceAlertModalOpen"
  | "PriceAlertCreate"
  | "PriceAlertUpdate"
  | "PriceAlertPause"
  | "PriceAlertResume"
  | "PriceAlertDelete"
  | "PriceAlertGroupPause"
  | "PriceAlertGroupResume"
  | "PriceAlertGroupDelete"
  | "PriceAlertNotificationClick"
  | "PriceAlertLandingPriceCompared"
  | "PriceAlertAutoCreatedToggle"
  | "PriceAlertAutoCreatedLoadMore"
  | "PriceAlertAutoCreatedDeleteAll"
  | "HotelDatesUpdate"
  | "HotelRoomConfigUpdate"
  | "HotelBookingClick"
  | "ViewFlightsSlideOut"
  | "FlightBookingEdit"
  | "FlightBookingView"
  | "FlightCardOpen"
  | "FlightCardClose"
  | "FlightSelect"
  | "FlightEdit"
  | "TripFlightAdd"
  | "TripFlightRemove"
  | "FlightDetailsView"
  | "FlightUpgradeSelect"
  | "FlightPriceSummaryOpen"
  | "FlightPriceSummaryClose"
  | "FlightBookingCreate"
  | "FlightBookingConfirmAndPay"
  | "FlightBookingConfirmAndPayError"
  | "FlightBookingCancel"
  | "ViewFlightBookingErrorPage"
  | "ViewFlightBookingPage"
  | "ViewFlightBookingReviewAndPayPage"
  | "ViewFlightBookingConfirmationPage"
  | "ViewInspirationSlideOut"
  | "InspirationUnPublish"
  | "InspirationPublish"
  | "InspirationEdit"
  | "InspirationItemEdit"
  | "InspirationUpdate"
  | "InspirationItemUpdate"
  | "InspirationItemInsert"
  | "InspirationDelete"
  | "InspirationDuplicate"
  | "InspirationItineraryGenerate"
  | "InspirationPreview"
  | "InspirationCopyLink"
  | "ChatShared"
  | "ChatCopyLink"
  | "TripShared"
  | "TripCopyLink"
  | "TripCopyInviteLink"
  | "TripSharedError"
  | "PlaceShared"
  | "PlaceSharedError"
  | "InspirationShared"
  | "InspirationSharedError"
  | "EventActorShared"
  | "EventActorSharedError"
  | "SharedTripOpen"
  | "SharedChatOpen"
  | "SharedTripPreview"
  | "SharedChatPreview"
  | "SendTripOpen"
  | "ContactUsCreate"
  | "ContactUsError"
  | "EmailVerifyResend"
  | "RedirectVanityUrl"
  | "RedirectVanityUrlError"
  | "ProcessVanityUrl"
  | "ProcessReferralUrl"
  | "InspirationFavoriteAdd"
  | "InspirationFavoriteRemove"
  | "InspirationFeaturedAdd"
  | "InspirationFeaturedRemove"
  | "InspirationBuried"
  | "InspirationPaymentRequired"
  | "InspirationPaymentFree"
  | "InspirationSearch"
  | "PlaceTypeAheadSearch"
  | "ErrorPage"
  | "EmbeddedWidgetOpen"
  | "EmbeddedWidgetClose"
  | "ClickPlaceLink"
  | "ClickExternalLink"
  | "ViewOrganizationPage"
  | "ViewAnswerIntelligenceTeaser"
  | "ClickAnswerIntelligenceTeaserCta"
  | "ClickGenerateQrCode"
  | "ClickDownloadQrCode"
  | "ClickHotelCTA"
  | "PlaceReviewAdd"
  | "PlaceReviewEdit"

  // Directions
  | "GetDirectionsTapped"

  // Creator
  | "CreatorJoinProgramComplete"
  | "CreatorGenerateMagicLink"
  | "CreatorCopyMagicLink"
  | "CreatorPreviewMagicLink"
  | "CreatorCreate"
  | "AdminCreate"
  | "CreatorGenerateGuide"
  | "CreatorGenerateMagicLinkError"
  | "CreatorGenerateGuideError"
  | "CreatorGenerateBlankGuide"

  // Bot generate
  | "InspirationSummaryGenerate"
  | "InspirationSectionSummaryGenerate"
  | "InspirationItemSummaryGenerate"

  // Start Anywhere
  | "ChatAttachmentBegin"
  | "ChatAttachmentAddLink"
  | "ChatAttachmentAddFile"

  // Give away
  | "ViewGiveAwayPage"
  | "GiveAwayJoin"
  | "GiveAwayAlreadyJoin"

  // Folio Document
  | "FolioDocumentEditEvent"

  // Explore
  | "ViewExplorePage"
  | "ViewGuidePage"
  | "ViewGuideDetailSlideOut"
  | "ExploreTypeAheadSearch"
  | "ExploreFilterByVibe"
  | "ExploreSearchSubmit"
  | "ExploreLocationChange"

  // Collections
  | "CollectionShare"
  | "CollectionCopyLink"
  | "CollectionCopyInviteLink"
  | "CollectionCreate"
  | "CollectionFavoriteAdd"
  | "CollectionFavoriteRemove"
  | "ImportCollectionClick"

  // Packages
  | "PackageOfferShown"
  | "PackageOfferSelected"
  | "PackageOfferAccepted"
  | "PackageInputsUpdated"

  // Filters (for Explore)
  | "ClickFilterButton"
  | "ClickConfirmFilters"

  // Media
  | "MediaAdd"
  | "MediaRemove"
  | "MediaAIAsk"

  // Promo dialogs
  | "ViewUpdateModal"
  | "ClickUpdateModalCTA"

  // Chat form prompts
  | "ViewChatFormPrompt"
  | "DismissChatFormPrompt"

  // Generic Dialog Open/Close
  | AmplitudeDialogName

  // Flights
  | "ClickFlightBookCTA"
  | "ClickFlightAddCTA"

  // App Store
  | "ClickedAppStoreButton"

export type AmplitudeDialogName =
  | "DialogDestinationOpen"
  | "DialogDestinationClose"
  | "DialogAuthOpen"
  | "DialogAuthClose"
  | "DialogForgotPasswordOpen"
  | "DialogForgotPasswordClose"
  | "DialogRegistrationGateOpen"
  | "DialogVerifyEmailOpen"
  | "DialogVerifyEmailClose"
  | "DialogSearchExploreOpen"
  | "DialogSearchExploreClose"
  | "DialogAppHomeSearchOpen"
  | "DialogAppHomeSearchClose"
  | "DialogWatchPriceModalOpen"
  | "DialogWatchPriceModalClose"
  | "DialogTripPreferencesOpen"
  | "DialogTripPreferencesClose"
  | "DialogOpen"
  | "DialogClose"

type AmplitudeDialogAnalyticsNameFromEvent<T extends string> =
  T extends `Dialog${infer Name}Open`
    ? Name extends ""
      ? never
      : `Dialog${Name}Close` extends AmplitudeDialogName
        ? Name
        : never
    : never

export type AmplitudeDialogAnalyticsName =
  AmplitudeDialogAnalyticsNameFromEvent<AmplitudeDialogName>

// Properties type for different objects
// Typically a subset of the fields as we are not interested in all fields
// for the purpose of analytics
export type AmplitudeChatProps = {
  id: number
  vertical?: string
}

export type AmplitudeTripProps = {
  id?: number
  name?: string
  roadTrip?: boolean
}

// Mirrors the backend user_class rule (Agent::Browser::SessionUtils#browser_session_tags) so
// frontend events partition the same way as the Kernel session tags and the
// [Server]/[Job] RestaurantBooking* events.
export type AmplitudeUserClass = "mt" | "non_mt" | "anon"

export type AmplitudeLocationProps = {
  entityRef: PlaceRef
  name: string
  type?: string
  city?: string
  state?: string
  countryCode?: string
  timeZone?: string
  url?: string
  categories?: PlaceCategory[] | GeoCategory[]
}

export type AmplitudeEventActorProps = {
  entity_ref: EventActorRef
  name: string
}

export type AmplitudeStopDetailsProps = {
  location: AmplitudeLocationProps
  days?: number
}

export type AmplitudeRoadtrip = {
  roadtrip: boolean
}

export type AmplitudeDayTimeProps = {
  dateTime: string
}

export type AmplitudeDateRangeProps = {
  startDate: string
  endDate: string
}

export type AmplitudeTimeFrameProps = {
  startDate?: string
  duration?: number
}

export type AmplitudeTravelersProps = TravelerGroup
export type AmplitudeRoomsProps = Room[]
export type AmplitudeBudgetProps = Budget
export type AmplitudeTripVibeProps = TripVibe

export type AmplitudeItineraryEventProps = {
  id: number
  travelers: TravelerGroup
  start: AmplitudeDayTimeProps
  end: AmplitudeDayTimeProps
  allDay: boolean
  place?: AmplitudeLocationProps
  title: string
  note?: string
  time_zone: string
  type: ItineraryEventType
  botGenerated: boolean
  hotelBooking?: AmplitudeHotelBookingProps
  bookedHotelRooms?: {
    totalRooms?: number
  }
  flight?: AmplitudeFlightSliceApiProps
}

export type AmplitudeUndoResponseProps = {
  operation: string
  event?: AmplitudeItineraryEventProps
  toastMessage?: string
}

export type AmplitudeRedoResponseProps = AmplitudeUndoResponseProps

export type AmplitudeFeedbackProps = {
  type: "entity" | "message" | "voice" | "agent_session"
  thumbsUp: string
  comment?: string
  entityRef?: EntityRef
}

export type AmplitudeSearchInputprops = {
  chat: AmplitudeChatProps
  placeType: string
  searchName: string
  placeRef: PlaceRef | undefined
  currPage: number
  budget: string | undefined
  categories: (EventCategory | HotelCategory)[] | undefined
  resultsCount: number
  startDate: string
  endDate: string
}

export type AmplitudeUserSearchProps = {
  query: string
  resultsCount: number
}

export type AmplitudeUserFollowProps = {
  targetUserRef: UserRef
}

export type AmplitudeHotelPriceDetails = {
  displayCurrency: string
  displayPrice: number
  displaySubTotal: number
  displayPropertyFee: number
  displayInsuranceFee: number
  displayTaxes: number
  displayTotal: number
  nights: number
}

export type AmplitudeAvailableRoomProps = {
  title: string
  description: string
  bedConfigs: BedConfig[]
  distributionType: DistributionType
  paymentType: PaymentType
  boardType: BoardType
  refundType?: RefundType | null
  occupancyLimit?: number | null
  availableCount?: number | null
  price: AmplitudeHotelPriceDetails
  checkIn: DayObject | undefined
  checkOut: DayObject | undefined
}

export type AmplitudeVendorDataProps = {
  title: string
  description: string
  price: AmplitudeHotelPriceDetails
}

export type AmplitudeHotelBookingProps = {
  ref?: HotelBookingRef
  status?: HotelBookingStatus
  provider_name?: string
  tripRef?: TripRef
  tripName?: string
  placeRef?: PlaceRef
  entityName?: string
  numNights: number
  numRooms: number
  numGuests: number
}

export type AmplitudeHotelBookingAndRoomProps = {
  hotelBooking: AmplitudeHotelBookingProps
  room: AmplitudeVendorDataProps
}

export type AmplitudeBookedHotelRoomProps = {
  totalRooms: number | undefined
}

export type AmplitudeAirportSummaryProps = {
  airport: {
    name: string
  }
}

export type AmplitudeFlightLegsProps = {
  [key: string]: {
    flightNumber: string
    dep?: AmplitudeAirportSummaryProps
    arr?: AmplitudeAirportSummaryProps
  }
}

export type AmplitudeFlightOptionProps = {
  flight: {
    tripType: "round-trip" | "one-way" | "multi-city"
    cheapest?: Optional<boolean>
    fastest?: Optional<boolean>
    favorite?: Optional<boolean>
    price: AmplitudeFlightPriceProps
    dep: AmplitudeAirportSummaryProps
    arr: AmplitudeAirportSummaryProps
    // Legs has too much data - remove for now
    // legs: AmplitudeFlightLegsProps
  }
}

export type AmplitudeFlightItineraryProps = {
  id: Nullable<number>
  tripType: "round-trip" | "one-way" | "multi-city"
  price: AmplitudeFlightPriceProps
  adults: number
  children: number
  slices: { [key: string]: AmplitudeFlightSliceProps }
}

export type AmplitudeFlightUpgradeProps = {
  name: string
  label: string
  price: AmplitudeFlightPriceProps
  priceDiff: number
}

export type AmplitudeFlightPriceProps = {
  base: number
  taxes: number
  fees: number
  taxes_and_fees: number
  total: number
  total_per_ticket?: number
  currency: string
}

export type AmplitudeFlightBookingProps = {
  id?: number
  status: FlightBookingStatus
  flight_itinerary?: AmplitudeItineraryEventProps
}

export type AmplitudeFlightBookingErrorProps = {
  id?: number
  status: FlightBookingStatus
  flight_itinerary?: AmplitudeItineraryEventProps
  error?: string
}

export type AmplitudeFlightSlicePriceProps = {
  slices: {
    [key: string]: AmplitudeFlightSliceApiProps
  }
  price: AmplitudeFlightPriceProps
}

export type AmplitudeFlightItinearyUpgradeProps = {
  slices: {
    [key: string]: AmplitudeFlightSliceApiProps
  }
  upgrade: AmplitudeFlightUpgradeProps
}

export type AmplitudeFlightSliceProps = {
  id: Nullable<number>
  stop: number
  depAt?: AmplitudeDayTimeProps
  dep?: AmplitudeAirportSummaryProps
  arrAt?: AmplitudeDayTimeProps
  arr?: AmplitudeAirportSummaryProps
}

export type AmplitudeFlightSliceApiProps = {
  id: Nullable<number>
  stop: number
  depAt?: AmplitudeDayTimeProps
  dep: AmplitudeAirportSummaryProps
  arrAt?: AmplitudeDayTimeProps
  arr: AmplitudeAirportSummaryProps
  // Legs has too much data - remove for now
  // legs: AmplitudeFlightLegsProps
}

export type AmplitudeFlightCTAProps = {
  airline: string
  price: number
  currency: string
  route: string
  tripType: "round-trip" | "one-way" | "multi-city"
  bookType?: "direct" | "mindtrip"
}

export type AmplitudeBotMessageProps = {
  id: number
  source: string
  intent?: Intent
}

export type AmplitudeCustomerMessageProps = {
  id: number
  source: string
  question: string
}

export type AmplitudeInspirationProps = {
  entity_ref: InspirationRef
  handle: string
  days: number
  place: string
  title: string
  template: InspirationTemplate
  status: InspirationStatus
  day_num?: number
  is_from_thatch: boolean
}

export interface AmplitudeInspirationUpdateProps extends AmplitudeInspirationProps {
  where: InspirationWhere
  block_type?: string
}

export type AmplitudeInspirationItemProps = {
  id: number
  place?: string
  title: string
  block_type: string
}

export interface AmplitudeInspirationItemUpdateProps extends AmplitudeInspirationItemProps {
  where: InspirationWhere
}

export interface AmplitudeInspirationBotGenerateProps extends AmplitudeInspirationProps {
  option: InspirationBotGenerateOption
}

export interface AmplitudeInspirationItemBotGenerateProps extends AmplitudeInspirationItemProps {
  option: InspirationBotGenerateOption
}

export type AmplitudeCollectionProps = {
  ref: CollectionRef
  name: string
  public: boolean
}

export type AmplitudeAttachmentMetaDataProps = {
  title?: string
  description?: string
  thumbnailUrl?: string
  handle?: string
}

export type AmplitudeAttachmentOperation =
  | "upload"
  | "paste"
  | "drag-drop"
  | "user-enter"

export type AmplitudeAttachmentProps = {
  type: AttachmentType
  url?: string
  metaData?: AmplitudeAttachmentMetaDataProps
  // data_type is unknown until it hits the server
  dataType?: AttachmentDataType
}

export type AmplitudeExploreFilterProps = {
  filter: string // JSON value of the filters
}

export type AmplitudeCommunityFilterProps =
  | typeof AmplitudeEmptyValue
  | SocialFilters["type"]

// Strongly typed the Event name and its properties
export type AmplitudeEventAndProperties =
  | EventAuthStepViewProperties
  | EventEmailAuthStepProperties
  | EventPhoneAuthProperties
  | EventAuthSuccessProperties
  | EventRouteChangeProperties
  | EventFoundTripAcceptProperties
  | EventFoundBookingAddProperties
  | EventVoiceInterviewSkipProperties
  | EventSubmitVerticalFormProperties
  | EventTopSuggestionInteractionProperties
  | EventOpenInChatProperties
  | EventGuideIdeasProperties
  | EventCollectionIdeasProperties
  | EventGuideVersionsProperties
  | EventPriceAlertClickProperties
  | EventCustomAgentInteractionProperties
  | EventLocationPickerProperties
  | EventRefineForYouStripeProperties
  | EventClickTripSuggestionProperties
  | EventSetUpAssistantStartProperties
  | EventSetUpAssistantStepViewProperties
  | EventSetUpAssistantStepTransitionProperties
  | EventSetUpAssistantExitProperties
  | EventSetUpAssistantCompleteProperties
  | EventSetUpAssistantCompletionContinueProperties
  | EventViewTripSheetProperties
  | AmplitudeViewHomePageEventProps
  | EventClickHomePageCtaProperties
  | ViewChatPageEventProps
  | EventChatPromptProperties
  | EventTripProperties
  | EventTripSpecificsExtractedProperties
  | EventTripJoinedProperties
  | EventFollowUpMessageRequestedProperties
  | EventClarifyingQuestionsProperties
  | EventIntentStopsProperties
  | EventIntentLocationProperties
  // | EventIntentDatesProperties -> replaced by EventTimeFrameProperties
  | EventIntentTravelersProperties
  | EventIntentBudgetProperties
  | EventRoadtripProperties
  | EventTimeFrameProperties
  | EventIntentTripVibeProperties
  | EventChatSearchProperties
  | EventUserSearchProperties
  | EventUserFollowProperties
  | EventUserUnfollowProperties
  | EventForgottenPasswordProperties
  | EventPlaceProperties
  | EventEntityListenProperties
  | EventViewPlaceDetailSectionProperties
  | EventFeedbackProperties
  | EventVoiceChatProperties
  | EventMessageCopyProperties
  | EventMessageListenProperties
  | EventMapHightLightProperties
  | EventItineraryEventProperties
  | EventItineraryChangeEventProperties
  | EventItineraryRemoveEventProperties
  | EventItineraryUndoEventProperties
  | EventItineraryRedoEventProperties
  | EventItineraryDuplicatedProperties
  | EventItineraryGenerateProperties
  | EventAskAIQuestionProperties
  | EventAskAIPlaceProperties
  | EventReserveProperties
  | EventDetailActionProperties
  | EventHotelBookingCreateProperties
  | ViewHotelBookingPageProperties
  | ViewHotelBookingErrorPageProperties
  | EventHotelBookingConfirmAndPayProperties
  | EventHotelBookingConfirmAndPayErrorProperties
  | EventHotelBookingPaymentStepProperties
  | ViewHotelRoomsPageProperties
  | EventCustomAgentProperties
  | EventPriceAlertProperties
  | HotelDatesUpdateProperties
  | HotelRoomConfigUpdateProperties
  | EventFlightOptionProperties
  | EventFlightItineraryEventProperties
  | EventFlightProperties
  | EventFlightUpgradeProperties
  | ViewFlightBookingPageProperties
  | ViewFlightBookingErrorPageProperties
  | EventFlightErrorProperties
  | EventClickFlightCTAProperties
  | EventInspirationProperties
  | EventInspirationUpdateProperties
  | EventInspirationItemUpdateProperties
  | EventInspirationGenerateItineraryProperties
  | EventChatShareProperties
  | EventChatCopyLinkProperties
  | EventTripShareProperties
  | EventTripCopyLinkProperties
  | EventPlaceShareProperties
  | EventInspirationShareProperties
  | EventSharedOpenProperties
  | EventSharedTripOpenProperties
  | EventContactUsCreateProperties
  | EventEmailVerifyProperties
  | EventRedirectVanityUrlProperties
  | EventProcessReferralUrlProperties
  | EventErrorPageProperties
  | EventOrganizationProperties
  | EventCreatorJoinProgramCompleteProperties
  | EventCreatorMagicLinkProperties
  | EventChatAttachmentProperties
  | EventGiveAwayProperties
  | EventFolioDocumentEditEventProperties
  | ViewOrganizationPageProperties
  | AnswerIntelligenceTeaserProperties
  | QrCodeProperties
  | ViewExplorePageProperties
  | EventInspirationBotGenerateProperties
  | EventInspirationItemBotGenerateProperties
  | EventCreatorCreateProperties
  | EventExploreFilterByVibeProperties
  | EventExploreSearchProperties
  | EventExploreLocationChangeProperties
  | EventInspirationSearchProperties
  | EventPlaceTypeAheadSearchProperties
  | EventExploreTypeAheadSearchProperties
  | EventCollectionCreateProperties
  | EventCollectionShareProperties
  | EventCollectionFavoriteProperties
  | EventImportCollectionClickProperties
  | EventPackageOfferShownProperties
  | EventPackageOfferSelectedProperties
  | EventPackageOfferAcceptedProperties
  | EventPackageInputsUpdatedProperties
  | EventClickChatPillProperties
  | EventClickAppHomeSearchPillProperties
  | EventClickActionPillProperties
  | EventClickTripAdviceActionPillProperties
  | EventDismissTripAdviceProperties
  | EventSnoozeTripAdviceProperties
  | EventUnsnoozeTripAdviceProperties
  | EventViewTripAdviceCardProperties
  | EventClickFilterButtonProperties
  | EventMediaAddProperties
  | EventEventActorShareProperties
  | EventEventActorViewProperties
  | EventDialogProperties
  | EventViewUpdateModalProperties
  | EventClickUpdateModalCTAProperties
  | EventChatFormPromptProperties
  | EventGetDirectionsTappedProperties
  | EventAppStoreProperties
  | EventPlaceReviewProperties
  | EventClickMemoryToastProperties
  | EventNotificationClickProperties
  | EventConnectCalendarProperties
  | EventGoogleCalendarConnectFailedProperties
  | EventCalendarSyncPhaseProperties
  | EventTravelStoryOpenedProperties
  | EventTravelStoryFrameViewedProperties
  | EventTravelStoryClosedProperties
  | EventTravelStoryDownloadProperties
  | EventTravelStoryShareProperties
  | EventTravelStoryPlanTripClickedProperties

export type AmplitudeBaseEventAndProperties = {
  name: AmplitudeEventWithProps
}

export type CalendarConnectionType = "Google" | "Apple"

export type SetUpAssistantStep =
  | "intro"
  | "basics"
  | "voice_personality"
  | "trip_in_mind"
  | "loyalty_programs"
  | "connect_accounts"
  | "favorite_places"
  | "interview_choice"
  | "setup_method"
  | "travel_style"
  | "accommodation"
  | "preferences"
  | "wrapping_up"
  | "complete"
  | "voice_interview"

export type SetUpAssistantFlow =
  | "calendar"
  | "setup_form"
  | "fallback_form"
  | "voice_interview"

export type SetUpAssistantStepAnalyticsProperties = {
  step: SetUpAssistantStep
  flow: SetUpAssistantFlow
  gcal_import_resume?: boolean
}

export interface EventSetUpAssistantStartProperties extends AmplitudeBaseEventAndProperties {
  name: "SetUpAssistantStart" | "UpdateAssistant"
  properties: {
    source: TravelAssistantEntrySource
  }
}

export interface EventSetUpAssistantStepViewProperties extends AmplitudeBaseEventAndProperties {
  name: "SetUpAssistantStepView"
  properties: SetUpAssistantStepAnalyticsProperties
}

export type SetUpAssistantLoyaltyAnswerProperties = {
  loyalty_answer: "yes" | "no"
  loyalty_selected_count: number
  loyalty_unlisted_count: number
  loyalty_unsaved_input_count: number
}

export interface EventSetUpAssistantStepTransitionProperties extends AmplitudeBaseEventAndProperties {
  name: "SetUpAssistantStepTransition"
  properties: {
    from_step: SetUpAssistantStep
    to_step: SetUpAssistantStep
    action:
      | "next"
      | "back"
      | "skip_interview"
      | "start_form"
      | "start_voice_interview"
      | "switch_voice_to_form"
      | "complete_form"
      | "connect_accounts"
      | "complete_calendar"
    from_flow: SetUpAssistantFlow
    to_flow: SetUpAssistantFlow
    gcal_import_resume?: boolean
  } & Partial<SetUpAssistantLoyaltyAnswerProperties>
}

export interface EventSetUpAssistantExitProperties extends AmplitudeBaseEventAndProperties {
  name: "SetUpAssistantExit"
  properties: SetUpAssistantStepAnalyticsProperties & {
    trigger: "close_button"
    action: "confirm_exit"
  }
}

export interface EventConnectCalendarProperties extends AmplitudeBaseEventAndProperties {
  name: "ConnectCalendarStart" | "ConnectCalendarComplete"
  properties: {
    type: CalendarConnectionType
  }
}

export interface EventGoogleCalendarConnectFailedProperties extends AmplitudeBaseEventAndProperties {
  name: "GoogleCalendarConnectFailed"
  properties: {
    reason: "access_denied" | "invalid_state" | "token_exchange" | "unknown"
  }
}

export interface EventCalendarSyncPhaseProperties extends AmplitudeBaseEventAndProperties {
  name: "CalendarSyncPhaseCompleted" | "CalendarSyncPhaseFailed"
  properties: {
    provider: CalendarSyncProvider
    phase: "initial_sync" | "preview_story"
  }
}

type TravelStoryAnalyticsProperties = {
  template_name: TravelStoryTemplateName
  frame_number: number
  slide_count: number
}

export interface EventTravelStoryOpenedProperties extends AmplitudeBaseEventAndProperties {
  name: "TravelStoryOpened"
  properties: TravelStoryAnalyticsProperties
}

export interface EventTravelStoryFrameViewedProperties extends AmplitudeBaseEventAndProperties {
  name: "TravelStoryFrameViewed"
  properties: TravelStoryAnalyticsProperties & {
    navigation_method:
      | "initial"
      | "automatic"
      | "next_button"
      | "previous_button"
      | "swipe"
      | "unknown"
  }
}

export interface EventTravelStoryClosedProperties extends AmplitudeBaseEventAndProperties {
  name: "TravelStoryClosed"
  properties: TravelStoryAnalyticsProperties & {
    reached_last_frame: boolean
  }
}

export interface EventTravelStoryDownloadProperties extends AmplitudeBaseEventAndProperties {
  name: "TravelStoryDownload"
  properties: {
    slide_count: number
    outcome: "completed" | "failed"
  }
}

export interface EventTravelStoryShareProperties extends AmplitudeBaseEventAndProperties {
  name: "TravelStoryShare"
  properties: {
    slide_count: number
    outcome: "completed" | "canceled" | "failed"
    format: "png_files" | "zip_file"
  }
}

export interface EventTravelStoryPlanTripClickedProperties extends AmplitudeBaseEventAndProperties {
  name: "TravelStoryPlanTripClicked"
  properties: TravelStoryAnalyticsProperties
}

export interface EventCustomAgentProperties extends AmplitudeBaseEventAndProperties {
  name: "AgentsPageView" | "CustomAgentTurnedOn" | "CustomAgentRunNow"
  properties: {
    agent_ref?: string
    source?: string
    kind?: "create" | "suggestion"
  }
}

export interface EventPriceAlertProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "ViewPriceAlertsPage"
    | "PriceAlertModalOpen"
    | "PriceAlertCreate"
    | "PriceAlertUpdate"
    | "PriceAlertPause"
    | "PriceAlertResume"
    | "PriceAlertDelete"
    | "PriceAlertGroupPause"
    | "PriceAlertGroupResume"
    | "PriceAlertGroupDelete"
    | "PriceAlertNotificationClick"
    | "PriceAlertLandingPriceCompared"
    | "PriceAlertAutoCreatedToggle"
    | "PriceAlertAutoCreatedLoadMore"
    | "PriceAlertAutoCreatedDeleteAll"
  properties: {
    agent_type: "hotel_price_alert" | "flight_price_alert"
    source?: string
    created_by?: string
    agent_id?: number
    entity_ref?: string | null
    hotel_name?: string
    alert_mode?: "fixed" | "percent"
    target_price?: number
    room_selection?: "any_room" | "single_room" | "multiple_rooms"
    room_type_count?: number
    created_count?: number
    failed_count?: number
    deleted_count?: number
    /** Hotels rolled up under one watched search. 0 means it has matched nothing yet. */
    alert_count?: number
    status?: string
    next_status?: string
    check_in?: string
    check_out?: string
    adults?: number
    children?: number
    is_open?: boolean
    next_page?: number
    proactive_agent_notification_id?: number
    alerted_price?: number
    current_price?: number
    price_delta?: number
    price_delta_percent?: number
    currency?: string
    comparison_outcome?: "higher" | "same_or_lower" | "unavailable"
    warning_shown?: boolean
  }
}

// Voice-to-voice chat mode (the realtime takeover, not the setup-assistant
// voice interview). Two events, one pair per attempt: Start fires on the
// voice-mode button click — before the mic prompt, so it counts intent and is
// the adoption denominator — and End reports only whether the attempt failed
// and how long it lasted. Every Start has an End, including attempts that
// never opened (reason "failed_to_start"), so outcomes are property filters on
// one event, never a subtraction between two. voice_session_id joins the pair
// and matches the local voice trace context.
export interface EventVoiceChatProperties extends AmplitudeBaseEventAndProperties {
  name: "VoiceSessionStart" | "VoiceSessionEnd"
  properties: {
    voice_session_id?: string
    // VoiceSessionEnd only — Start fires before the click knows which chat it
    // will land in (it may still have to create one).
    chat_id?: number
    // left_chat = the user left the page the call lives on (a different chat,
    // or navigating off a non-chat page the call started on); chat_started =
    // the hang-up-first rule, the user sent a message instead of speaking;
    // failed_to_start = the mic or chat-creation gate, i.e. voice mode never
    // opened.
    reason?: "user_ended" | "left_chat" | "chat_started" | "failed_to_start"
    // Time in voice mode, measured from the overlay opening (unset when it
    // never opened), so the permission-prompt wait is not billed as session
    // minutes.
    duration_ms?: number
    // Where the open flow died, or "connection" when a live session's
    // transport dropped mid-call (the customer may still close the overlay
    // themselves afterward, so reason alone can't say the call died). Unset
    // when nothing failed. failure_expected marks customer-environment mic
    // outcomes (denied permission, missing device) vs engineering-relevant
    // errors.
    failure?: "mic" | "chat_create" | "connection" | "audio_profile"
    failure_expected?: boolean
    // VoiceSessionEnd only — the failures the session survived silently.
    // start_retry_count: silent start retries that fired (0 or 1 today);
    // benign_error_count: provider error events classified benign and
    // swallowed mid-call. Both always emitted (0 included) so averages read
    // straight off the property. benign_error_codes lists the distinct
    // provider codes seen, only when there were any — the tripwire for a
    // "benign" code spiking in the field.
    start_retry_count?: number
    benign_error_count?: number
    benign_error_codes?: string[]
    // VoiceSessionEnd only: mid-call transport deaths the session survived by
    // rebuilding, and the time those rebuilds took in total. A drop the ladder
    // could not rescue ends with failure: "connection" instead.
    reconnect_count?: number
    reconnect_gap_ms?: number
    // VoiceSessionEnd only: barge-ins that cancelled the assistant and then
    // transcribed to nothing — noise and speaker echo taken for the customer —
    // over the assistant speech they interrupted. Both always emitted (0
    // included). turn_detection_type and noise_reduction are what the provider
    // session resolved to, so the rate can be read per regime while old tabs
    // still send their own overrides.
    phantom_barge_in_count?: number
    short_barge_in_count?: number
    assistant_audio_ms?: number
    turn_detection_type?: "server_vad" | "semantic_vad"
    noise_reduction?: "near_field" | "far_field"
  }
}

export type AmplitudeHomeAnchor = "howitworks" | "new" | "inspire"

export type EventViewTripSheetProperties = {
  name:
    | "ViewTripSheetItinerary"
    | "ViewTripSheetCalendar"
    | "ViewTripSheetBookings"
    | "ViewTripSheetMedia"
    | "ViewTripSheetIdeas"
    | "ViewTripSheetDetails"
    | "ViewTripSheetChats"
    | "ViewTripSheetDisruptions"
    | "ViewTripSheetKeyDetails"
    | "TripKeyDetailUserAdd"
    | "TripKeyDetailUserUpdate"
    | "TripKeyDetailUserRemove"
    | "TripKeyDetailSuggestionAccepted"
    | "TripKeyDetailSuggestionRejected"
  properties: {
    context: string
  }
}

export type AmplitudeViewHomePageEventProps = {
  name: "ViewHomePage"
  properties?: {
    anchor?: AmplitudeHomeAnchor
    herovariant?: string
  }
}

export type HomepageCta =
  | "hero"
  | "learns"
  | "acts"
  | "adapts"
  | "explore_guides"
  | "creator_community"
  | "closing"

export type HomepageCtaAction = "setup" | "get_started"

export interface EventClickHomePageCtaProperties extends AmplitudeBaseEventAndProperties {
  name: "ClickHomePageCta"
  properties: {
    cta: HomepageCta
    /** Set when the same placement switches between setup and get started. */
    action?: HomepageCtaAction
  }
}

export interface ViewChatPageEventProps extends AmplitudeBaseEventAndProperties {
  name:
    | "ViewChatPage"
    | "ViewChatGroupPage"
    | "ViewChatSearchPage"
    | "ChatCreate"
    | "ChatRemove"
    | "ChatRename"
    | "ChatConvertedToGroup"
    | "ChatSwitch"
  properties: {
    chat: AmplitudeChatProps
  }
}
export interface EventChatPromptProperties extends AmplitudeBaseEventAndProperties {
  name: "ChatUserMessageAdd"
  properties: {
    prompt: string
    chatType: "group" | "individual"
    attachment?: AmplitudeAttachmentProps
    chat: AmplitudeChatProps
    // Set (only to true) for turns spoken in voice-to-voice mode
    voice_chat?: true
  }
}

export interface EventFollowUpMessageRequestedProperties extends AmplitudeBaseEventAndProperties {
  name: "ChatFollowUpMessageRequested"
  properties: {
    lastCustomerMessageId?: number
    lastBotMessageId: number
    inspirationId?: number
    chat: AmplitudeChatProps
  }
}

export type ClarifyingQuestionPresentation =
  | "form"
  | "instant_submit"
  | "chat_answerable"

export type EventClarifyingQuestionsBaseProperties = {
  messageId: number
  questionCount: number
  inputTypes: QuestionInputType[]
  presentation: ClarifyingQuestionPresentation
}

export type EventClarifyingQuestionsProperties =
  | {
      name: "ViewClarifyingQuestions"
      properties: EventClarifyingQuestionsBaseProperties
    }
  | {
      name: "SubmitClarifyingQuestions"
      properties: EventClarifyingQuestionsBaseProperties & {
        answeredCount: number
        usedOther: boolean
      }
    }
  | {
      name: "SkipClarifyingQuestions"
      properties: EventClarifyingQuestionsBaseProperties
    }
  | {
      name: "ClickClarifyingQuestionOther"
      properties: {
        messageId: number
        questionIndex: number
        inputType: QuestionInputType
      }
    }

export interface EventIntentLocationProperties extends AmplitudeBaseEventAndProperties {
  name: "IntentLocationUpdate" | "LocationSelect" | "TripDestinationUpdate"
  properties: {
    location: AmplitudeLocationProps
  }
}

export interface EventIntentStopsProperties extends AmplitudeBaseEventAndProperties {
  name: "MajorStopsUpdate"
  properties: {
    stops: AmplitudeStopDetailsProps[]
  }
}

// export interface EventIntentDatesProperties
//   extends AmplitudeBaseEventAndProperties {
//   name: "IntentDatesUpdate"
//   properties: AmplitudeDateRangeProps
// }

export interface EventTripProperties extends AmplitudeBaseEventAndProperties {
  name: "ViewTripHub" | "TripCreate" | "TripUpdate"
  properties: {
    trip: AmplitudeTripProps
  }
}

export interface EventTripSpecificsExtractedProperties extends AmplitudeBaseEventAndProperties {
  name: "TripSpecificsExtracted"
  properties: {
    search_type:
      | "stays"
      | "flights"
      | "restaurants"
      | "activity"
      | "events"
      | "trip"
    where?: boolean
    from?: boolean
    to?: boolean
    when?: boolean
    who?: boolean
    roadtrip?: boolean
  }
}

export interface EventTripJoinedProperties extends AmplitudeBaseEventAndProperties {
  name: "TripJoined"
  properties: {
    code: string
    trip: TripRef
  }
}

export interface EventRoadtripProperties extends AmplitudeBaseEventAndProperties {
  name: "TripRoadtripUpdate"
  properties: {
    roadtrip: AmplitudeRoadtrip
  }
}

export interface EventTimeFrameProperties extends AmplitudeBaseEventAndProperties {
  name: "IntentTimeFrameUpdate" | "TripTimeFrameUpdate"
  properties: {
    timeFrame: AmplitudeTimeFrameProps
  }
}

export interface EventIntentTravelersProperties extends AmplitudeBaseEventAndProperties {
  name: "IntentTravelersUpdate" | "TripTravelersUpdate"
  properties: {
    travelers: AmplitudeTravelersProps
  }
}
export interface EventPackageInputsUpdatedProperties extends AmplitudeBaseEventAndProperties {
  name: "PackageInputsUpdated"
  properties: {
    service: string
    rooms: AmplitudeRoomsProps
    carRental: boolean
    location: AmplitudeLocationProps | undefined
    tripRef: TripRef
  }
}

export interface EventPackageOfferShownProperties extends AmplitudeBaseEventAndProperties {
  name: "PackageOfferShown"
  properties: {
    service: string
    tripRef: TripRef
  }
}

export interface EventPackageOfferSelectedProperties extends AmplitudeBaseEventAndProperties {
  name: "PackageOfferSelected"
  properties: {
    service: string
    tripRef: TripRef
  }
}

export interface EventPackageOfferAcceptedProperties extends AmplitudeBaseEventAndProperties {
  name: "PackageOfferAccepted"
  properties: {
    service: string
    tripRef: TripRef
  }
}

export interface EventIntentBudgetProperties extends AmplitudeBaseEventAndProperties {
  name: "IntentBudgetUpdate" | "TripBudgetUpdate"
  properties: {
    budget: Budget
  }
}

export interface EventIntentTripVibeProperties extends AmplitudeBaseEventAndProperties {
  name: "TripVibeUpdate"
  properties: {
    vibe: TripVibe
  }
}

export interface EventChatSearchProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "ChatHotelSearch"
    | "ChatRestaurantSearch"
    | "ChatEventSearch"
    | "ChatAttractionSearch"
    | "ChatActivitySearch"
  properties: AmplitudeSearchInputprops
}

export interface EventUserSearchProperties extends AmplitudeBaseEventAndProperties {
  name: "UserSearch"
  properties: AmplitudeUserSearchProps
}

export interface EventUserFollowProperties extends AmplitudeBaseEventAndProperties {
  name: "UserFollow"
  properties: AmplitudeUserFollowProps
}

export interface EventUserUnfollowProperties extends AmplitudeBaseEventAndProperties {
  name: "UserUnfollow"
  properties: AmplitudeUserFollowProps
}

export interface EventForgottenPasswordProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "ForgotPasswordFail"
    | "ResetPasswordFail"
    | "RegisterUserFail"
    | "SignInFail"
  properties: {
    error?: string
  }
}

export interface EventPlaceProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "PlaceFavoriteAdd"
    | "PlaceFavoriteRemove"
    | "PlaceShared"
    | "ViewPlaceDetailSlideOut"
    | "ViewPlaceDetailAllPhotos"
    | "ViewPlaceDetailStandalone"
  properties: {
    location: AmplitudeLocationProps
    urlPath?: string
    ctaButtonLabel?: string | null
    context?: string
    tests?: AmplitudeTest[]
  }
}

export interface EventEntityListenProperties extends AmplitudeBaseEventAndProperties {
  name: "EntityListen"
  properties: {
    script: VoiceGuideScript
  }
}

export interface EventViewPlaceDetailSectionProperties extends AmplitudeBaseEventAndProperties {
  name: "ViewPlaceDetailSection"
  properties: {
    section: string
    location: AmplitudeLocationProps
  }
}

export interface EventFeedbackProperties extends AmplitudeBaseEventAndProperties {
  name: "UserFeedbackCreate"
  properties: {
    feedBack: AmplitudeFeedbackProps
  }
}

export interface EventMessageCopyProperties extends AmplitudeBaseEventAndProperties {
  name: "MessageCopy"
  properties: {
    messageId: number
  }
}

export interface EventMessageListenProperties extends AmplitudeBaseEventAndProperties {
  name: "MessageListen"
  properties: {
    messageId: number
  }
}

export interface EventMapHightLightProperties extends AmplitudeBaseEventAndProperties {
  name: "MapHighlightAdd" | "MapHighlightRemove"
  properties: {
    messageId: number
    highlightCount: number
  }
}

export interface EventItineraryEventProperties extends AmplitudeBaseEventAndProperties {
  name: "ItineraryAdd" | "ItineraryRemove"
  properties: {
    event: AmplitudeItineraryEventProps
    toastMessage?: string
    uiWhere?: UIWhere
    context?: AddToTripTrackingContext
    target?: string
  }
}

export interface EventItineraryChangeEventProperties extends AmplitudeBaseEventAndProperties {
  name: "ItineraryUpdate"
  properties: {
    // from: { event: AmplitudeItineraryEventProps }
    // to:
    event: AmplitudeItineraryEventProps
    toastMessage?: string
    uiWhere: UIWhere
    target?: string
  }
}

export interface EventItineraryRemoveEventProperties extends AmplitudeBaseEventAndProperties {
  name: "ItineraryRemoveScheduledEvents" | "ItineraryRemoveIdeas"
  properties: {
    toastMessage?: string
    uiWhere: UIWhere
  }
}

export interface EventItineraryDuplicatedProperties extends AmplitudeBaseEventAndProperties {
  name: "ItineraryEventDuplicated"
  properties: {
    tripRef: TripRef
    eventType: ItineraryEventType
  }
}

export interface EventItineraryUndoEventProperties extends AmplitudeBaseEventAndProperties {
  name: "ItineraryUndo"
  properties: AmplitudeUndoResponseProps
  uiWhere: UIWhere
}

export interface EventItineraryRedoEventProperties extends AmplitudeBaseEventAndProperties {
  name: "ItineraryRedo"
  properties: AmplitudeRedoResponseProps
  uiWhere: UIWhere
}

export interface EventItineraryGenerateProperties extends AmplitudeBaseEventAndProperties {
  name: "AIItineraryGenerate"
  properties: {
    message: AmplitudeBotMessageProps | AmplitudeCustomerMessageProps
  }
}

export interface EventAskAIQuestionProperties extends AmplitudeBaseEventAndProperties {
  name: "AIQuestionAsk"
  properties: {
    message: {
      id: number
      question: string
    }
    uiWhere: UIWhere
  }
}

export type AmplitudeAskAIPlaceType = "sample" | "custom" | "pill" | "recommend"

export interface EventAskAIPlaceProperties extends AmplitudeBaseEventAndProperties {
  name: "AIPlaceAsk"
  properties: {
    location?: AmplitudeLocationProps
    type: AmplitudeAskAIPlaceType
  }
}

export interface EventReserveProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "FlightBookingEdit"
    | "FlightBookingView"
    | "HotelBookingView"
    | "RestaurantBookingReserveForMeClick"
    | "RestaurantBookDirect"
    | "PlaceBookDirect"
    | "ClickPlaceLink"
    | "ClickHotelCTA"
    | "ClickExternalLink"
  properties: {
    supplier?: string
    url?: string
    location?: AmplitudeLocationProps
    user_class?: AmplitudeUserClass
  }
}

export interface EventDetailActionProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "HotelChooseRoomView"
    | "ViewFlightsSlideOut"
    | "ActivitiesAndToursView"
    | "ActivitiesView"
    | "ToursView"
  properties: {
    location: AmplitudeLocationProps
  }
}

export interface EventHotelBookingCreateProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "HotelBookingCreate"
    | "HotelBookingError"
    | "HotelBookingClick"
    | "OutOfRangeBooking"
  properties: {
    error?: string
    hotelBooking?: Pick<AmplitudeHotelBookingProps, "provider_name">
    room: AmplitudeAvailableRoomProps
  }
}

export interface ViewHotelBookingPageProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "ViewHotelBookingPage"
    | "ViewHotelBookingConfirmationPage"
    | "ViewHotelBookingDetailsPage"
  properties: {
    error?: string
    hotelBooking?: AmplitudeHotelBookingProps
    room: AmplitudeVendorDataProps
  }
}

export interface ViewOrganizationPageProperties extends AmplitudeBaseEventAndProperties {
  name: "ViewOrganizationPage"
  properties: {
    tab: string
    // sub-navigation within a tab
    subtab?: string
  }
}

export interface AnswerIntelligenceTeaserProperties extends AmplitudeBaseEventAndProperties {
  name: "ViewAnswerIntelligenceTeaser" | "ClickAnswerIntelligenceTeaserCta"
  properties: {
    orgid: string
    cta?: "turn_on"
  }
}

export interface QrCodeProperties extends AmplitudeBaseEventAndProperties {
  name: "ClickGenerateQrCode" | "ClickDownloadQrCode"
  properties: {
    orgid: string
    cta: string
    withLogo: boolean
  }
}

export interface ViewHotelBookingErrorPageProperties extends AmplitudeBaseEventAndProperties {
  name: "ViewHotelBookingErrorPage"
  properties: {
    error?: string
    hotelBooking?: AmplitudeHotelBookingProps
    room: AmplitudeVendorDataProps
  }
}

export interface EventHotelBookingConfirmAndPayProperties extends AmplitudeBaseEventAndProperties {
  name: "HotelBookingConfirmAndPay" | "HotelBookingCancel"
  properties: {
    hotelBooking: AmplitudeHotelBookingProps
    room: AmplitudeVendorDataProps
  }
}

export interface EventHotelBookingConfirmAndPayErrorProperties extends AmplitudeBaseEventAndProperties {
  name: "HotelBookingConfirmAndPayError" | "HotelBookingCancelError"
  properties: {
    error: HotelBookingError | string
    hotelBooking: AmplitudeHotelBookingProps
    room: AmplitudeVendorDataProps
  }
}

export interface EventHotelBookingPaymentStepProperties extends AmplitudeBaseEventAndProperties {
  name: "HotelBookingPaymentStep"
  properties: AmplitudeHotelBookingAndRoomProps & {
    payment: {
      provider: "nuitee"
      integration: "stripe_elements"
      stage:
        | "payment_session"
        | "payment_element_load"
        | "stripe_confirmation"
        | "payment_verification"
        | "payment_processing_timeout"
        | "book_completion"
      outcome: "started" | "ready" | "succeeded" | "pending" | "failed"
      errorType?: string
      errorCode?: string
      declineCode?: string
      paymentIntentStatus?: string
    }
  }
}

export interface ViewHotelRoomsPageProperties extends AmplitudeBaseEventAndProperties {
  name: "ViewHotelRoomsPage"
  properties: {
    location: AmplitudeLocationProps
    dates: { checkIn: DayObject; checkOut: DayObject }
    travelers: AmplitudeTravelersProps
  }
}

export interface HotelDatesUpdateProperties extends AmplitudeBaseEventAndProperties {
  name: "HotelDatesUpdate"
  properties: {
    location: AmplitudeLocationProps
    dates: { checkIn: DayObject; checkOut: DayObject }
  }
}

export interface HotelRoomConfigUpdateProperties extends AmplitudeBaseEventAndProperties {
  name: "HotelRoomConfigUpdate"
  properties: {
    location: AmplitudeLocationProps
    travelers: AmplitudeTravelersProps
  }
}

export interface EventFlightOptionProperties extends AmplitudeBaseEventAndProperties {
  name: "FlightCardOpen" | "FlightCardClose" | "FlightSelect" | "FlightEdit"
  properties: AmplitudeFlightOptionProps
}

export interface EventFlightItineraryEventProperties extends AmplitudeBaseEventAndProperties {
  name: "TripFlightAdd" | "TripFlightRemove"
  properties: {
    event: AmplitudeItineraryEventProps
  }
}

export interface EventFlightProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "FlightDetailsView"
    | "FlightPriceSummaryOpen"
    | "FlightPriceSummaryClose"
    | "FlightBookingCreate"
  properties: {
    flight: AmplitudeFlightSlicePriceProps
  }
}

export interface EventFlightErrorProperties extends AmplitudeBaseEventAndProperties {
  name: "FlightBookingConfirmAndPayError"
  properties: {
    error: string
  }
}

export interface EventFlightUpgradeProperties extends AmplitudeBaseEventAndProperties {
  name: "FlightUpgradeSelect"
  properties: {
    flight: AmplitudeFlightItinearyUpgradeProps
  }
}

export interface ViewFlightBookingPageProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "ViewFlightBookingPage"
    | "ViewFlightBookingReviewAndPayPage"
    | "ViewFlightBookingConfirmationPage"
    | "FlightBookingConfirmAndPay"
    | "FlightBookingCancel"
  properties: AmplitudeFlightBookingProps
}

export interface ViewFlightBookingErrorPageProperties extends AmplitudeBaseEventAndProperties {
  name: "ViewFlightBookingErrorPage"
  properties: AmplitudeFlightBookingErrorProps
}

export interface EventClickFlightCTAProperties extends AmplitudeBaseEventAndProperties {
  name: "ClickFlightBookCTA" | "ClickFlightAddCTA"
  properties?: AmplitudeFlightCTAProps
}

export interface EventInspirationProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "ViewInspirationSlideOut"
    | "InspirationUnPublish"
    | "InspirationPublish"
    | "InspirationDelete"
    | "InspirationDuplicate"
    | "ItineraryPublish"
    | "CollectionPublish"
    | "InspirationFavoriteAdd"
    | "InspirationFavoriteRemove"
    | "InspirationFeaturedAdd"
    | "InspirationFeaturedRemove"
    | "InspirationBuried"
    | "InspirationPaymentRequired"
    | "InspirationPaymentFree"
    | "InspirationPreview"
    | "InspirationCopyLink"
    | "ViewGuidePage"
    | "ViewGuideDetailSlideOut"
  properties: {
    inspiration: AmplitudeInspirationProps
    publicGuide?: boolean
    urlPath?: string
    context?: string
  }
}

export interface EventInspirationUpdateProperties extends AmplitudeBaseEventAndProperties {
  name: "InspirationEdit" | "InspirationUpdate"
  properties: {
    inspiration: AmplitudeInspirationUpdateProps
  }
}

export interface EventInspirationItemUpdateProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "InspirationItemEdit"
    | "InspirationItemUpdate"
    | "InspirationItemInsert"
  properties: {
    inspirationItem: AmplitudeInspirationItemUpdateProps
  }
}

export interface EventInspirationGenerateItineraryProperties extends AmplitudeBaseEventAndProperties {
  name: "InspirationItineraryGenerate"
  properties: {
    inspiration: AmplitudeInspirationProps
    chat: AmplitudeChatProps
  }
}

export interface EventInspirationBotGenerateProperties extends AmplitudeBaseEventAndProperties {
  name: "InspirationSummaryGenerate" | "InspirationSectionSummaryGenerate"
  properties: {
    inspirationItem: AmplitudeInspirationBotGenerateProps
  }
}

export interface EventInspirationItemBotGenerateProperties extends AmplitudeBaseEventAndProperties {
  name: "InspirationItemSummaryGenerate"
  properties: {
    inspirationItem: AmplitudeInspirationItemBotGenerateProps
  }
}

export interface EventChatShareProperties extends AmplitudeBaseEventAndProperties {
  name: "ChatShared"
  properties: {
    chat: AmplitudeChatProps
  }
}

export interface EventChatCopyLinkProperties extends AmplitudeBaseEventAndProperties {
  name: "ChatCopyLink"
  properties: {
    chat: AmplitudeChatProps
  }
}

export interface EventTripShareProperties extends AmplitudeBaseEventAndProperties {
  name: "TripShared" | "TripSharedError"
  properties: {
    emails: string[]
    note: string
    kind: ShareKind
  }
}

export interface EventTripCopyLinkProperties extends AmplitudeBaseEventAndProperties {
  name: "TripCopyLink" | "TripCopyInviteLink"
  properties: {
    trip: AmplitudeTripProps
  }
}

export interface EventPlaceShareProperties extends AmplitudeBaseEventAndProperties {
  name: "PlaceShared" | "PlaceSharedError"
  properties: {
    place: AmplitudeLocationProps
    target: string
    emails?: string[]
    note?: string
  }
}

export interface EventInspirationShareProperties extends AmplitudeBaseEventAndProperties {
  name: "InspirationShared" | "InspirationSharedError"
  properties: {
    inspiration: AmplitudeInspirationProps
    target: string
    emails?: string[]
    note?: string
  }
}

export interface EventEventActorShareProperties extends AmplitudeBaseEventAndProperties {
  name: "EventActorShared" | "EventActorSharedError"
  properties: {
    event_actor: AmplitudeEventActorProps
    target: string
    emails?: string[]
    note?: string
  }
}

export interface EventEventActorViewProperties extends AmplitudeBaseEventAndProperties {
  name: "ViewEventActorDetailSlideOut" | "ViewEventActorDetailStandalone"
  properties: {
    event_actor: AmplitudeEventActorProps
    urlPath?: string
    context?: string
  }
}

export interface EventSharedOpenProperties extends AmplitudeBaseEventAndProperties {
  name: "SharedChatPreview" | "SharedChatOpen" | "SendTripOpen"
  properties: {
    code: string
    chat: AmplitudeChatProps
  }
}

export interface EventSharedTripOpenProperties extends AmplitudeBaseEventAndProperties {
  name: "SharedTripPreview" | "SharedTripOpen"
  properties: {
    code: string
    trip: AmplitudeTripProps
  }
}

export interface EventErrorPageProperties extends AmplitudeBaseEventAndProperties {
  name: "ErrorPage"
  properties: {
    code?: string
    title?: string
    description?: string
    version?: string
    path?: string
    referrer?: string
  }
}

export interface EventContactUsCreateProperties extends AmplitudeBaseEventAndProperties {
  name: "ContactUsCreate" | "ContactUsError"
  properties: {
    email: string
  }
}

export interface EventEmailVerifyProperties extends AmplitudeBaseEventAndProperties {
  name: "EmailVerifyResend"
  properties: {
    code?: string
    handle?: string
  }
}

export interface EventRedirectVanityUrlProperties extends AmplitudeBaseEventAndProperties {
  name: "RedirectVanityUrl" | "RedirectVanityUrlError" | "ProcessVanityUrl"
  properties: {
    code: string
    partner?: string
    asset?: {
      title?: string
      url?: string
      mode?: string
      dayCount?: string
    }
    chat?: AmplitudeChatProps
  }
}

export interface EventProcessReferralUrlProperties extends AmplitudeBaseEventAndProperties {
  name: "ProcessReferralUrl"
  properties: {
    referType: "guide" | "entity"
    referrer: UserRef
  }
}

export interface EventOrganizationProperties extends AmplitudeBaseEventAndProperties {
  name: "EmbeddedWidgetOpen" | "EmbeddedWidgetClose"
  properties: {
    org_name: string
    geo_name?: string
    source_url: string
    action_cue?: string
    message_cue?: string
    user_message?: string
    media_url?: string
    cta_id?: string
    cta_type?: string
    open_trigger?: string
    host_referrer?: string
    click_ids?: string
  }
}

export interface EventCreatorJoinProgramCompleteProperties extends AmplitudeBaseEventAndProperties {
  name: "CreatorJoinProgramComplete"
  properties: {
    skipPayment: boolean
  }
}

export interface EventCreatorCreateProperties extends AmplitudeBaseEventAndProperties {
  name: "CreatorCreate" | "AdminCreate"
  properties: {
    kind: "guide" | "link" | "any"
  }
}

export interface EventCreatorMagicLinkProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "CreatorGenerateMagicLink"
    | "CreatorCopyMagicLink"
    | "CreatorPreviewMagicLink"
    | "CreatorGenerateGuide"
    | "CreatorGenerateMagicLinkError"
    | "CreatorGenerateGuideError"
    | "CreatorGenerateBlankGuide"
  properties: {
    vanityUrl: {
      url: string
      mode?: string
      type?: string
      title?: string
      numOfDays?: string
    }
  }
}

export interface EventChatAttachmentProperties {
  name:
    | "ChatAttachmentBegin"
    | "ChatAttachmentAddLink"
    | "ChatAttachmentAddFile"
  properties: {
    chat?: AmplitudeChatProps
    operation?: AmplitudeAttachmentOperation
    attachment?: AmplitudeAttachmentProps
  }
}

export interface EventGiveAwayProperties {
  name: "ViewGiveAwayPage" | "GiveAwayJoin" | "GiveAwayAlreadyJoin"
  properties: {
    promo: string
  }
}

export interface EventFolioDocumentEditEventProperties extends AmplitudeBaseEventAndProperties {
  name: "FolioDocumentEditEvent"
  properties: {
    event: AmplitudeItineraryEventProps
  }
}

export interface ViewExplorePageProperties extends AmplitudeBaseEventAndProperties {
  name: "ViewExplorePage"
  properties: {
    type: ExploreType
  }
}

export interface EventExploreFilterByVibeProperties extends AmplitudeBaseEventAndProperties {
  name: "ExploreFilterByVibe"
  properties: {
    name: string
    context: string
  }
}

export interface EventExploreSearchProperties extends AmplitudeBaseEventAndProperties {
  name: "ExploreSearchSubmit"
  properties: {
    search: string
    context: string
  }
}

export interface EventExploreLocationChangeProperties extends AmplitudeBaseEventAndProperties {
  name: "ExploreLocationChange"
  properties: {
    context: string
  }
}

export interface EventInspirationSearchProperties extends AmplitudeBaseEventAndProperties {
  name: "InspirationSearch"
  properties: {
    search: string
    total: number
    status?: InspirationStatus
    handle?: string
  }
}

export interface EventPlaceTypeAheadSearchProperties extends AmplitudeBaseEventAndProperties {
  name: "PlaceTypeAheadSearch"
  properties: {
    search: string
    total: number
    uiWhere: string
  }
}

export interface EventExploreTypeAheadSearchProperties extends AmplitudeBaseEventAndProperties {
  name: "ExploreTypeAheadSearch"
  properties: {
    search: string
    total: number
    context: string
  }
}

export interface EventCollectionCreateProperties extends AmplitudeBaseEventAndProperties {
  name: "CollectionCreate"
  properties: {
    trigger?: CollectionTrigger
    importUrl?: string
    public: boolean
  }
}

export interface EventImportCollectionClickProperties extends AmplitudeBaseEventAndProperties {
  name: "ImportCollectionClick"
  properties: {
    type: CollectionImportType
  }
}

export interface EventCollectionShareProperties extends AmplitudeBaseEventAndProperties {
  name: "CollectionShare" | "CollectionCopyLink" | "CollectionCopyInviteLink"
  properties: {
    target: string
  }
}

export interface EventCollectionFavoriteProperties extends AmplitudeBaseEventAndProperties {
  name: "CollectionFavoriteAdd" | "CollectionFavoriteRemove"
  properties: {
    collectionRef: CollectionRef
    context: string
    urlPath: string
  }
}

export interface EventSetUpAssistantCompleteProperties extends AmplitudeBaseEventAndProperties {
  name: "SetUpAssistantComplete"
  properties: {
    method: "voice" | "form" | "calendar"
    voice_provider?: "elevenlabs" | "openai"
    voice_interview_flow?: "setup" | "update_memories"
  }
}

export type SetUpAssistantCompletionContinueProperties =
  | { method: "form" | "calendar" }
  | {
      method: "voice"
      ending_source: "tool" | "customer" | "expired"
      call_connected: boolean
      assistant_audio_playing: boolean
      closing_finished: boolean
      ms_since_ending?: number
      voice_interview_session_id?: string
    }

export interface EventSetUpAssistantCompletionContinueProperties extends AmplitudeBaseEventAndProperties {
  name: "SetUpAssistantCompletionContinue"
  properties: SetUpAssistantCompletionContinueProperties
}

export interface EventClickChatPillProperties extends AmplitudeBaseEventAndProperties {
  name: "ClickChatPill"
  properties: {
    title: string
  }
}

export interface EventClickAppHomeSearchPillProperties extends AmplitudeBaseEventAndProperties {
  name: "ClickAppHomeSearchPill"
  properties: {
    type: "stays" | "flights" | "restaurants" | "activity" | "events" | "trip"
    label: string
  }
}

export interface EventSubmitVerticalFormProperties extends AmplitudeBaseEventAndProperties {
  name: "SubmitVerticalForm"
  properties: {
    type: AppHomeSearchType
    who?: TravelerGroup
    when: TimeFrame
    where?: string[]
    from?: string
    to?: string
    flight_trip_type?: FlightTripTypes
    flight_leg_count?: number
    free_form_text: string
  }
}

export interface EventTopSuggestionInteractionProperties extends AmplitudeBaseEventAndProperties {
  name: "TopSuggestionInteraction"
  properties: {
    suggestion_ref: string
    topic: string
    pill_type?: string
    pill_ref?: string
    index?: number
  } & (
    | { type: "clickCTA" | "paginate"; dismiss_reason?: never }
    | {
        type: "dismiss"
        dismiss_reason: "done" | "not_now" | "not_interested" | "other"
      }
  )
}

export interface EventOpenInChatProperties extends AmplitudeBaseEventAndProperties {
  name: "OpenInChat"
  properties: {
    source: "action_pill" | "inspiration" | "place_question" | "agent_report"
    chat_id: number
    answer_key?: string
    run_ref?: string
  }
}

export interface EventGuideIdeasProperties extends AmplitudeBaseEventAndProperties {
  name: "GuideIdeasOpen" | "GuideIdeasMessage" | "GuideIdeasAddPlace"
  properties: {
    inspiration_ref: string
    orgid?: string
    chat_id?: number
    entity_ref?: string
    // GuideIdeasMessage: the starter prompt's key, or "typed"
    prompt?: string
    // GuideIdeasAddPlace: the surface the add came from (cards when absent)
    context?: string
  }
}

export interface EventCollectionIdeasProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "CollectionIdeasOpen"
    | "CollectionIdeasMessage"
    | "CollectionIdeasAddPlace"
  properties: {
    collection_ref: string
    chat_id?: number
    entity_ref?: string
    // CollectionIdeasMessage: the starter prompt's key, or "typed"
    prompt?: string
    context?: string
  }
}

export interface EventGuideVersionsProperties extends AmplitudeBaseEventAndProperties {
  name: "GuideVersionHistoryOpen" | "GuideVersionRestore"
  properties: {
    inspiration_ref: string
    orgid?: string
    // GuideVersionRestore: the restored version
    version_id?: number
  }
}

export interface EventPriceAlertClickProperties extends AmplitudeBaseEventAndProperties {
  name: "PriceAlertClick"
  properties: {
    type: "flight" | "hotel"
    interaction: "view" | "dismiss" | "edit" | "delete"
    run_ref: string
  }
}

export interface EventCustomAgentInteractionProperties extends AmplitudeBaseEventAndProperties {
  name: "CustomAgentInteraction"
  properties: {
    type:
      | "manage"
      | "turn_on"
      | "edit"
      | "refine"
      | "view_results"
      | "dismiss"
      | "pause"
      | "run_now"
      | "delete"
      | "create"
      | "suggest_more"
      | "history"
    source: "home" | "agents" | "report"
    agent_ref?: string
    suggestion_ref?: string
    run_ref?: string
  }
}

export interface EventLocationPickerProperties extends AmplitudeBaseEventAndProperties {
  name: "LocationPicker"
  properties: {
    location_ref?: string
    source: "home"
  } & (
    | { type: "view_map" | "hide_map" | "select_place" }
    | {
        type: "toggle_near"
        previous_geo_search_type: "within_geo" | "from_geo_center" | "near_me"
        geo_search_type: "within_geo" | "from_geo_center"
      }
  )
}

export interface EventRefineForYouStripeProperties extends AmplitudeBaseEventAndProperties {
  name: "RefineForYouStripe"
  properties: {
    type: "edit" | "submit"
    ribbon_ref?: string
    location_ref: string
    refine_instructions?: string
  }
}

export interface EventClickTripSuggestionProperties extends AmplitudeBaseEventAndProperties {
  name: "ClickTripSuggestion"
  properties: { location_ref: string; ribbon_ref: string }
}

export interface EventClickActionPillProperties extends AmplitudeBaseEventAndProperties {
  name: "ClickActionPill"
  properties: {
    type: ActionItem["type"] | "return_to_chat" | "view_trip"
    label: string
    context?: "setup_complete"
    setup_method?: "voice" | "form"
  }
}

export interface EventClickTripAdviceActionPillProperties extends AmplitudeBaseEventAndProperties {
  name: "ClickTripAdviceActionPill"
  properties: {
    body: string
    cardPosition?: number
    context: "TripHub" | "AppHomeTripCard"
    label: string
    tripBacked: boolean
  }
}

export interface EventDismissTripAdviceProperties extends AmplitudeBaseEventAndProperties {
  name: "DismissTripAdvice"
  properties: {
    body: string
    context: "TripHub" | "AppHomeTripCard"
    reason: string
    tripBacked: boolean
  }
}

export interface EventSnoozeTripAdviceProperties extends AmplitudeBaseEventAndProperties {
  name: "SnoozeTripAdvice"
  properties: {
    context: "TripHub" | "AppHomeTripCard"
  }
}

export interface EventUnsnoozeTripAdviceProperties extends AmplitudeBaseEventAndProperties {
  name: "UnsnoozeTripAdvice"
  properties: {
    context: "TripHub" | "AppHomeTripCard"
  }
}

export interface EventViewTripAdviceCardProperties extends AmplitudeBaseEventAndProperties {
  name: "ViewTripAdviceCard"
  properties: {
    body: string
    cardPosition?: number
    context: "TripHub" | "AppHomeTripCard"
    tripBacked: boolean
  }
}

export interface EventClickFilterButtonProperties extends AmplitudeBaseEventAndProperties {
  name: "ClickFilterButton" | "ClickConfirmFilters"
  properties: {
    tab: string
    filters?: AmplitudeExploreFilterProps
    social?: SocialFilters["type"] | typeof AmplitudeEmptyValue
    context: string
  }
}

export interface EventMediaAddProperties extends AmplitudeBaseEventAndProperties {
  name: "MediaAdd" | "MediaRemove" | "MediaAIAsk"
  properties: {
    kind: "guide" | "collection" | "file" | "video" | "link"
    tripRef?: TripRef
    inspiration?: AmplitudeInspirationProps
    collection?: AmplitudeCollectionProps
    attachment?: AmplitudeAttachmentProps
  }
}

export interface EventViewUpdateModalProperties extends AmplitudeBaseEventAndProperties {
  name: "ViewUpdateModal"
  properties: {
    type: PromoDialogType
  }
}

export interface EventClickUpdateModalCTAProperties extends AmplitudeBaseEventAndProperties {
  name: "ClickUpdateModalCTA"
  properties: {
    type: PromoDialogType
  }
}

export interface EventChatFormPromptProperties extends AmplitudeBaseEventAndProperties {
  name: "ViewChatFormPrompt" | "DismissChatFormPrompt"
  properties: {
    type: "assistant-setup"
  }
}

export interface EventDialogProperties extends AmplitudeBaseEventAndProperties {
  name: AmplitudeDialogName
  properties: {
    dialog: {
      title: string
      subTitle: string
    }
  }
}

export interface EventGetDirectionsTappedProperties extends AmplitudeBaseEventAndProperties {
  name: "GetDirectionsTapped"
  properties: {
    place: {
      entityRef: string
      name: string
    }
  }
}

export interface EventAppStoreProperties extends AmplitudeBaseEventAndProperties {
  name: "ClickedAppStoreButton"
  properties: {
    clickId: {
      ttclid?: string
      fbclid?: string
      gclid?: string
    }
  }
}

export interface EventPlaceReviewProperties extends AmplitudeBaseEventAndProperties {
  name: "PlaceReviewAdd" | "PlaceReviewEdit"
  properties: {
    rating: number
    textLength: number
  }
}

export interface EventClickMemoryToastProperties extends AmplitudeBaseEventAndProperties {
  name: "ClickMemoryToast"
  properties: {
    memory_ref: string
    action: "created" | "updated"
    surface?: "toast" | "list"
  }
}

export type NotificationClickSurface = "notification_list" | "in_app_toast"

export interface EventNotificationClickProperties extends AmplitudeBaseEventAndProperties {
  name: "NotificationClick"
  properties: {
    surface: NotificationClickSurface
    type: Notification["type"]
    durability: "persistent" | "ephemeral"
    notification_id?: number
    already_read?: boolean
    trip_ref?: TripRef
    chat_id?: number
    collection_ref?: CollectionRef
    proactive_agent_id?: number
  }
}

export interface EventVoiceInterviewSkipProperties extends AmplitudeBaseEventAndProperties {
  name: "VoiceInterviewSkip"
  properties: {
    stage: "before_start" | "starting" | "in_progress" | "error"
    interview_started: boolean
    interview_message_count: number
    committed_voice_turn_count: number
  }
}

export interface EventRouteChangeProperties extends AmplitudeBaseEventAndProperties {
  name: "RouteChange"
  properties: {
    from: string
    to: string
    total_ms: number
    data_ms: number
    render_ms: number
    // one per Server-Timing phase MtSSP reports, e.g. server_session_ms
    [serverPhase: `server_${string}_ms`]: number
  }
}

export type PhoneAuthStep = "phone_number" | "code" | "optional_email"

export interface EventAuthStepViewProperties extends AmplitudeBaseEventAndProperties {
  name: "AuthStepView"
  properties: {
    method: "phone" | "email"
    step:
      | PhoneAuthStep
      | "email"
      | "signin"
      | "register"
      | "forgotten"
      | "verify_email"
      | "reset_password"
    phone_country?: string
    surface?: "page" | "dialog"
  }
}

// Email lookup responses and local validation; auth responses keep the historical SignIn/RegisterUser events.
export interface EventEmailAuthStepProperties extends AmplitudeBaseEventAndProperties {
  name: "AuthStepSubmit" | "AuthStepResult"
  properties: {
    method: "email"
    step: "email" | "signin" | "register"
    outcome?: "success" | "failure"
    error_type?: "validation" | "request_failed"
    next_step?: "signin" | "register"
  }
}

// Step results describe the browser response, not SMS delivery or an authoritative auth completion.
export interface EventPhoneAuthProperties extends AmplitudeBaseEventAndProperties {
  name:
    | "PhoneAuthSubmit"
    | "PhoneAuthResult"
    | "PhoneAuthUseEmail"
    | "PhoneAuthRestart"
    | "PhoneAuthEmailSkipped"
  properties: {
    step: PhoneAuthStep
    phone_country: string
    outcome?: "success" | "failure"
    error_type?:
      | "invalid_number"
      | "unsupported_country"
      | "expired"
      | "validation"
      | "access_denied"
      | "api_error"
      | "network_or_unknown"
  }
}

// Browser received a successful auth response. Preserve these names for historical UI funnels; backend events count issued credentials.
export interface EventAuthSuccessProperties extends AmplitudeBaseEventAndProperties {
  name: "SignIn" | "RegisterUser"
  properties: {
    method: "phone" | "email" | "google" | "apple"
    phone_country?: string
  }
}

/** A trip found in Gmail or calendar was added: as a new trip, or into an existing one. */
export interface EventFoundTripAcceptProperties extends AmplitudeBaseEventAndProperties {
  name: "FoundTripAccept"
  properties: {
    kind: "new" | "existing"
    bookingCount: number
  }
}

/** Bookings found after a trip was accepted were added to it from the trip page. */
export interface EventFoundBookingAddProperties extends AmplitudeBaseEventAndProperties {
  name: "FoundBookingAdd"
  properties: {
    bookingCount: number
  }
}
